import * as PIXI from 'pixi.js';
import type { WallBox } from '../../../domain/wallGuides';
import type { AlphaMask } from '../render/alphaMask';
import type { WindowSnapResult } from '../../../domain/windowSnap';

/**
 * Mutable PIXI room scene state that used to live as ~40 useRefs in RoomCanvas.
 * Grouped by meaning so extracted hooks take a small group instead of a bag of refs.
 *
 * Access pattern in the component:
 *   const scene = useRef(createRoomSceneState());
 *   scene.current.sprites.wallLeft  // was wallLeftSpriteRef.current
 *
 * Hooks receive the nested group object (stable identity for the lifetime of the
 * scene ref) and mutate fields directly — no .current on the group itself.
 *
 * RoomDragState / AdminTileMode are re-declared here (structurally identical to
 * dragSession / useRoomActions) so this module has no import cycle with the hooks
 * that consume its groups.
 */

/** Structurally identical to dragSession.RoomDragState (single writer: drag session). */
export interface RoomSceneDragState {
  isDragging: boolean;
  interiorId: number;
  marker: PIXI.Container;
  startX: number;
  startY: number;
  startWorldX: number;
  startWorldY: number;
  offsetX: number;
  offsetY: number;
  origX: number;
  origY: number;
  previousWindowSnap?: WindowSnapResult;
  /** 'mouse' | 'touch' | 'pen' — a finger hides what it drags, a mouse does not. */
  pointerType?: string;
  /** Screen px the item rides above the finger (domain/touchLift), fixed at drag start. */
  liftPx?: number;
  /** Picked up by holding the finger still — a drag that has not moved yet. */
  pickedUp?: boolean;
  /** The finger actually travelled past the drag threshold. */
  moved?: boolean;
  liftStartedAt?: number;
  liftProgress?: number;
  lastInvalid?: boolean;
  /** Wall items: height on the wall chosen by the drag (wall_floor_offset). */
  wallOffset?: number;
  /** Wall items: the other wall items as smart-guide targets (interaction/wallGuideDrag). */
  wallGuideBoxes?: WallBox[];
  wallGuideKey?: string;
  /** A second finger turned the gesture into a camera one: put the item back, save nothing. */
  cancelled?: boolean;
}

/** Structurally identical to useRoomActions.AdminTileMode. */
export type RoomSceneAdminTileMode = 'footprint' | 'surface' | null;

// ── Room decoration sprites (walls / floor) ──
export interface RoomSceneSprites {
  wallLeft: PIXI.Sprite | null;
  wallRight: PIXI.Sprite | null;
  floor: PIXI.Sprite | null;
}

// ── Backdrop catalog + texture bookkeeping ──
export interface RoomSceneBackdrop {
  activeWallCatalogId: number | null;
  activeFloorCatalogId: number | null;
  /** Catalog ids of the currently-placed custom wall/floor (null = room default). */
  initialWallFloorCatalogIds: { wall: number | null; floor: number | null };
  /** Texture paths loaded for this interior — unloaded on cleanup to free GPU memory. */
  loadedTexturePaths: Set<string>;
}

// ── Furniture markers + render-pass counter ──
export interface RoomSceneFurniture {
  markers: Map<number, PIXI.Container>;
  renderPass: number;
  /** The room has been shown with all its furniture (the first pass builds it hidden). */
  revealed: boolean;
}

// ── Drag / pointer interaction session ──
export interface RoomSceneDrag {
  state: RoomSceneDragState | null;
  transparentDuringDrag: Map<PIXI.Container, number>;
  draggingCarriedIds: Set<number>;
  furnitureJustTapped: boolean;
  /** Suspend undo auto-capture for the duration of a multi-write drag gesture. */
  captureSuspended: boolean;
}

// ── Selection highlight (green outline) ──
export interface RoomSceneHighlight {
  greenOutline: PIXI.Graphics | null;
  pendingCatalogId: number | null;
  marker: PIXI.Container | null;
  prevPreviewCatalogIds: Set<number>;
}

// ── Overlay Graphics nodes ──
export interface RoomSceneOverlays {
  tileGrid: PIXI.Graphics | null;
  occupiedTiles: PIXI.Graphics | null;
  dragFp: PIXI.Graphics | null;
  /** Smart-guide lines while a wall item is dragged. */
  wallGuides: PIXI.Graphics | null;
  cobwebFp: PIXI.Graphics | null;
  adminTile: PIXI.Graphics | null;
  adminDragPreview: PIXI.Graphics | null;
}

// ── Flags mirrored from React state (avoid stale closures in PIXI handlers) ──
export interface RoomSceneFlags {
  showGridMode: boolean;
  buildMode: boolean;
}

// ── Admin tile editor (footprint / surface) ──
export interface RoomSceneAdmin {
  tileMode: RoomSceneAdminTileMode;
  selectedTiles: Set<string>;
  tileItemIsMirrored: boolean;
  tileCenter: { tileX: number; tileY: number } | null;
  dragStartTile: { tileX: number; tileY: number } | null;
  dragMode: 'add' | 'remove';
  clickStart: {
    x: number;
    y: number;
    roomX: number;
    roomY: number;
    timestamp: number;
  } | null;
}

// ── Decode caches ──
export interface RoomSceneCaches {
  alphaMask: Map<string, AlphaMask | null>;
}

// ── PostHog interior load timing ──
export interface RoomSceneAnalytics {
  interiorEnterTs: number;
  didTrackInteriorFullyLoaded: boolean;
}

// ── Mirror-action debug traces ──
export interface RoomSceneMirrorTrace {
  seq: number;
  map: Map<number, { traceId: string; startedAt: number; targetMirror: boolean }>;
}

export interface RoomSceneState {
  sprites: RoomSceneSprites;
  backdrop: RoomSceneBackdrop;
  furniture: RoomSceneFurniture;
  drag: RoomSceneDrag;
  highlight: RoomSceneHighlight;
  overlays: RoomSceneOverlays;
  flags: RoomSceneFlags;
  admin: RoomSceneAdmin;
  caches: RoomSceneCaches;
  analytics: RoomSceneAnalytics;
  mirrorTrace: RoomSceneMirrorTrace;
}

export function createRoomSceneState(): RoomSceneState {
  return {
    sprites: {
      wallLeft: null,
      wallRight: null,
      floor: null,
    },
    backdrop: {
      activeWallCatalogId: null,
      activeFloorCatalogId: null,
      initialWallFloorCatalogIds: { wall: null, floor: null },
      loadedTexturePaths: new Set(),
    },
    furniture: {
      markers: new Map(),
      renderPass: 0,
      revealed: false,
    },
    drag: {
      state: null,
      transparentDuringDrag: new Map(),
      draggingCarriedIds: new Set(),
      furnitureJustTapped: false,
      captureSuspended: false,
    },
    highlight: {
      greenOutline: null,
      pendingCatalogId: null,
      marker: null,
      prevPreviewCatalogIds: new Set(),
    },
    overlays: {
      tileGrid: null,
      occupiedTiles: null,
      dragFp: null,
      wallGuides: null,
      cobwebFp: null,
      adminTile: null,
      adminDragPreview: null,
    },
    flags: {
      showGridMode: false,
      buildMode: false,
    },
    admin: {
      tileMode: null,
      selectedTiles: new Set(),
      tileItemIsMirrored: false,
      tileCenter: null,
      dragStartTile: null,
      dragMode: 'add',
      clickStart: null,
    },
    caches: {
      alphaMask: new Map(),
    },
    analytics: {
      interiorEnterTs: 0,
      didTrackInteriorFullyLoaded: false,
    },
    mirrorTrace: {
      seq: 0,
      map: new Map(),
    },
  };
}
